import { Injectable } from '@angular/core';
import { BehaviorSubject, interval } from 'rxjs';
import { AppStateService, CACHE_INIT_KEY, KEY_APP_STATE, STATE } from 'viggo-sistemas-core-web';

@Injectable({
  providedIn: 'root'
})
export class CacheService {
  private cacheStatus = new BehaviorSubject<'healthy' | 'degraded' | 'failed'>(
    'healthy'
  );
  private cacheName = 'cache-buster-woundcaresys';
  private maxCacheSize = 50 * 1024 * 1024; // 50MB por padrão
  private maxCacheAge = 7 * 24 * 60 * 60 * 1000; // 7 dias em milissegundos

  // Expõe o status do cache como Observable
  public cacheStatus$ = this.cacheStatus.asObservable();

  // Lista de chaves locais que são verificadas para integridade
  private criticalLocalStorageKeys: string[] = [
    'token',
    'user',
    'menu_items',
    'domain'
  ];

  // Valores mínimos esperados para cada chave (pode ser comprimento mínimo ou estrutura JSON esperada)
  private localStorageKeyRequirements: Record<string, any> = {
    token: { type: 'json' },
    user: { type: 'json' },
    menu_items: { type: 'array_json' },
    domain: { type: 'json' }
  };

  // Evento emitido quando uma chave crítica está ausente ou inválida
  private localStorageIntegrity = new BehaviorSubject<boolean>(true);
  public localStorageIntegrity$ = this.localStorageIntegrity.asObservable();

  // Último diagnóstico de integridade
  private lastIntegrityIssue: { time: number; keys: string[] } | null = null;

  constructor(protected appState: AppStateService) {
    this.appState.setCacheStatus(STATE.CACHE_INITIALIZING);

    setTimeout(() => this.verifyCacheReadiness(), 2000);

    // Verificar chaves críticas periodicamente
    this.setupPeriodicKeyCheck();
  }

  /**
   * Verifica se o cache está pronto e atualiza o estado
   */
  private async verifyCacheReadiness(): Promise<boolean> {
    try {
      // Verificar se o cache está acessível e funcionando
      const isReady = await this.checkCacheStorage();

      // Atualizar o status do cache no AppState
      if (isReady) {
        this.appState.setCacheStatus(STATE.CACHE_READY);
        this.cacheStatus.next('healthy');
      } else {
        this.appState.setCacheStatus(STATE.CACHE_FAILED);
        this.cacheStatus.next('degraded');
      }

      return isReady;
    } catch (error) {
      console.error('Erro ao verificar prontidão do cache:', error);
      this.appState.setCacheStatus(STATE.CACHE_FAILED);
      this.cacheStatus.next('failed');
      return false;
    }
  }

  /**
   * Configura verificação periódica das chaves críticas
   */
  private setupPeriodicKeyCheck(): void {
    // Verificar a cada 30 segundos
    interval(30000).subscribe(() => {
      this.checkLocalStorageIntegrity();
    });

    // Verificar também ao retornar de visibilidade (aba/janela volta a ficar ativa)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.checkLocalStorageIntegrity();
      }
    });
  }

  /**
   * Verifica a integridade das chaves do localStorage
   * @returns Array de chaves com problemas, vazio se estiver tudo ok
   */
  checkLocalStorageIntegrity(): string[] {
    const problematicKeys: string[] = [];

    if (
      this.appState && localStorage.getItem(KEY_APP_STATE) &&
      (!this.appState.isAppStable() || !this.appState.currentState)
    ) {
      return problematicKeys;
    }

    for (const key of this.criticalLocalStorageKeys) {
      try {
        const value = localStorage.getItem(key);

        // Verificar se existe
        if (value === null || value === '') {
          problematicKeys.push(key);
          // console.warn(`Chave crítica ausente ou vazia: ${key}`);
          continue;
        }

        // Verificações específicas com base nos requisitos
        const requirements = this.localStorageKeyRequirements[key];
        if (requirements) {
          // Verificar comprimento mínimo
          if (requirements.minLength && value.length < requirements.minLength) {
            problematicKeys.push(key);
            // console.warn(`Chave ${key} não atende ao comprimento mínimo`);
            continue;
          }

          // Verificar se é JSON válido com propriedades obrigatórias
          if (
            requirements.type === 'json' ||
            requirements.type === 'array_json'
          ) {
            try {
              const parsed = JSON.parse(value);

              // Verificar propriedades obrigatórias
              if (requirements.requiredProps) {
                const missingProps = requirements.requiredProps.filter(
                  (prop: string) => parsed[prop] === undefined
                );

                if (missingProps.length > 0) {
                  problematicKeys.push(key);
                  // console.warn(
                  //   `Chave ${key} não contém propriedades obrigatórias: ${missingProps.join(
                  //     ', '
                  //   )}`
                  // );
                  continue;
                }
              }
            } catch (e) {
              problematicKeys.push(key);
              // console.warn(`Chave ${key} não contém JSON válido`);
              continue;
            }
          }
        }
      } catch (e) {
        problematicKeys.push(key);
        console.error(`Erro ao verificar chave ${key}:`, e);
      }
    }

    // Atualizar status de integridade
    const isIntegrityOk = problematicKeys.length === 0;

    // Se houver problemas, registrar o momento e as chaves afetadas
    if (!isIntegrityOk) {
      this.lastIntegrityIssue = {
        time: Date.now(),
        keys: [...problematicKeys]
      };
      this.localStorageIntegrity.next(false);
    } else {
      this.localStorageIntegrity.next(true);
    }

    return problematicKeys;
  }

  /**
   * Retorna o último problema de integridade detectado
   */
  getLastIntegrityIssue(): { time: number; keys: string[] } | null {
    return this.lastIntegrityIssue;
  }

  /**
   * Limpa o cache para um arquivo específico
   */
  async clearCacheForFile(filename: string): Promise<boolean> {
    try {
      const cache = await caches.open(this.cacheName);
      const keys = await cache.keys();
      let deleteCount = 0;

      for (const key of keys) {
        if (key.url.includes(filename)) {
          await cache.delete(key);
          deleteCount++;
        }
      }

      console.log(
        `Cache limpo para ${filename}: ${deleteCount} entradas removidas`
      );
      this.cacheStatus.next('healthy');
      return true;
    } catch (error) {
      console.error('Erro ao limpar cache:', error);
      this.cacheStatus.next('degraded');
      return false;
    }
  }

  /**
   * Limpa todo o cache
   */
  async clearAllCache(): Promise<boolean> {
    try {
      const cache = await caches.open(this.cacheName);
      const keys = await cache.keys();

      for (const key of keys) {
        await cache.delete(key);
      }

      console.log(
        `Cache completamente limpo: ${keys.length} entradas removidas`
      );
      this.cacheStatus.next('healthy');
      return true;
    } catch (error) {
      console.error('Erro ao limpar todo o cache:', error);
      this.cacheStatus.next('failed');
      return false;
    }
  }

  /**
   * Verifica a saúde do cache
   */
  async checkCacheHealth(): Promise<boolean> {
    if (
      this.appState && localStorage.getItem(KEY_APP_STATE) &&
      (!this.appState.isAppStable() || !this.appState.currentState)
    ) {
      return true;
    }

    // Se o cache está em inicialização ou foi recentemente inicializado
    if (localStorage.getItem(CACHE_INIT_KEY) && this.appState.wasCacheInitializedRecently(10000)) { // 10 segundos
      return true;
    }

    try {
      // Verificar o cache
      const cacheHealth = await this.checkCacheStorage();

      // Verificar localStorage
      const problematicKeys = this.checkLocalStorageIntegrity();
      const localStorageHealth =
        problematicKeys.length === 0 && this.checkLocalStorageHealth();

      // Manutenção: limpar entradas antigas
      this.performCacheMaintenance();

      // Consideramos saudável apenas se ambos estiverem ok
      const isHealthy = cacheHealth && localStorageHealth;

      // Atualizar o status do cache no AppState
      this.appState.setCacheStatus(isHealthy ? STATE.CACHE_READY : STATE.CACHE_FAILED);

      this.cacheStatus.next(isHealthy ? 'healthy' : 'degraded');

      return isHealthy;
    } catch (error) {
      console.error('Erro ao verificar saúde do cache:', error);
      this.cacheStatus.next('failed');
      return false;
    }
  }

  /**
   * Verifica apenas a parte de cache
   */
  private async checkCacheStorage(): Promise<boolean> {
    try {
      // Verificar se podemos acessar o cache
      const cache = await caches.open(this.cacheName);

      // Tentar uma operação simples
      const testUrl = '/health-check';
      await cache.put(testUrl, new Response('OK'));
      const response = await cache.match(testUrl);
      await cache.delete(testUrl);

      return response !== undefined;
    } catch (error) {
      console.error('Erro ao verificar cache storage:', error);
      return false;
    }
  }

  /**
   * Verifica a saúde do localStorage, incluindo chaves críticas
   */
  private checkLocalStorageHealth(): boolean {
    try {
      // Teste básico de operação
      localStorage.setItem('health-check', 'OK');
      const basicOk = localStorage.getItem('health-check') === 'OK';
      localStorage.removeItem('health-check');

      if (!basicOk) {
        return false;
      }

      // Verificar chaves críticas
      let allKeysOk = true;
      const missingKeys: string[] = [];

      for (const key of this.criticalLocalStorageKeys) {
        const value = localStorage.getItem(key);
        if (value === null) {
          allKeysOk = false;
          missingKeys.push(key);
          continue;
        }

        // Para chaves que contêm JSON, verificar se é válido
        if (
          key.includes('config') ||
          key.includes('preferences') ||
          key.endsWith('json')
        ) {
          try {
            JSON.parse(value);
          } catch (e) {
            allKeysOk = false;
            missingKeys.push(key);
          }
        }
      }

      // if (!allKeysOk) {
      // console.warn(
      //   'Chaves críticas ausentes ou inválidas no localStorage:',
      //   missingKeys
      // );
      // }

      return allKeysOk;
    } catch (error) {
      console.error('Erro ao verificar localStorage:', error);
      return false;
    }
  }

  /**
   * Obtém a lista de chaves críticas do localStorage
   */
  getCriticalLocalStorageKeys(): string[] {
    return [...this.criticalLocalStorageKeys];
  }

  /**
   * Adiciona uma chave à lista de chaves críticas
   */
  addCriticalLocalStorageKey(key: string, requirements?: any): void {
    if (!this.criticalLocalStorageKeys.includes(key)) {
      this.criticalLocalStorageKeys.push(key);

      if (requirements) {
        this.localStorageKeyRequirements[key] = requirements;
      }
    }
  }

  /**
   * Realiza manutenção no cache (remove entradas antigas/excedentes)
   */
  private async performCacheMaintenance(): Promise<void> {
    try {
      const cache = await caches.open(this.cacheName);
      const keys = await cache.keys();
      const now = Date.now();

      // Verificar tamanho aproximado do cache
      let totalSize = 0;
      const entriesInfo: Array<{
        key: Request;
        size: number;
        timestamp: number;
      }> = [];

      for (const key of keys) {
        const response = await cache.match(key);
        if (response) {
          const clone = response.clone();
          const blob = await clone.blob();
          const size = blob.size;

          // Obter timestamp do cabeçalho ou usar estimativa
          let timestamp = now;
          const dateHeader = response.headers.get('date');
          if (dateHeader) {
            timestamp = new Date(dateHeader).getTime();
          }

          totalSize += size;
          entriesInfo.push({ key, size, timestamp });
        }
      }

      // Ordenar por mais antigo primeiro
      entriesInfo.sort((a, b) => a.timestamp - b.timestamp);

      // Remover entradas antigas
      for (const entry of entriesInfo) {
        const age = now - entry.timestamp;

        if (age > this.maxCacheAge) {
          await cache.delete(entry.key);
          totalSize -= entry.size;
        }
      }

      // Se ainda estiver muito grande, remover mais entradas (mais antigas primeiro)
      if (totalSize > this.maxCacheSize) {
        for (const entry of entriesInfo) {
          await cache.delete(entry.key);
          totalSize -= entry.size;

          if (totalSize <= this.maxCacheSize * 0.8) {
            // Reduzir até 80% do máximo
            break;
          }
        }
      }
    } catch (error) {
      console.warn('Erro durante manutenção do cache:', error);
    }
  }
}
